/**
 * CanonicalBackend — wraps a `Backend` and routes canonical
 * provider-row reads through the Client-scoped cell-data store
 * so multiple sheets share fetches.
 *
 * A row is **canonical** when its `lookupKey` starts with `:~`,
 * matching one of the routes declared in
 * `lona-rows/src/routes.rs` (`:~weather:{loc}`,
 * `:~whoop:{authId}:sleep`, `:~google:{authId}:calendar:{calId}:events`,
 * …). Provider handlers on lona-rows own those cells; on the
 * client, the Client-scoped `SourceRow.CellDataStore<string>`
 * is the single source of truth that every subscribing Sheet
 * reads through.
 *
 * Read-path integration:
 *   - `fetchCells` for a canonical row checks the store first;
 *     hit → returns stored cells, no network. Miss → delegates
 *     to the inner backend, populates the store, then returns
 *     the fetched cells. Every subscribing Sheet sees the new
 *     cells via its canonicalCells fan-out subscription
 *     (`Sheet` constructor).
 *   - `onCellChange` for a canonical row subscribes to the
 *     store instead of the inner backend — pushes translated
 *     into `CellChange` land on every sheet simultaneously.
 *   - Non-canonical rows pass through unchanged.
 *
 * Write-path integration:
 *   Every outbound path (`writeCell`, `clearCell`, `addRow`, …)
 *   passes through to the inner backend. Canonical rows reject
 *   user writes at the `SourceRow.write` layer; Backend-level
 *   writes (e.g. a test harness simulating a provider push via
 *   `MemoryRowsBackend.writeCell`) are legitimate — they do need to
 *   reach the inner backend's subscriber stream so the adapter's
 *   `onCellChange` translation fires.
 *
 * Typical composition (in lona-js's `Client`):
 *   ```
 *   const backend = new CanonicalBackend(
 *     new NormalizingBackend(httpBackend, sheet),
 *     { canonicalCells, lookup },
 *   );
 *   ```
 *
 * Rationale: provider-backed rows are sheet-invariant — two sheets that
 * name the same canonical key want the same cells — so one Client-scoped
 * store holds them and every `Sheet` views it (the shared canonical store).
 * See `.docs/content/cell-stores-and-handles.md`.
 */

import type { Option } from "../core/option.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { DateTime, Utc } from "@tento-chrono";
import type { NaiveDate } from "@tento-chrono";
import type { SheetsNetwork } from "../wire/rows-network.ts";

import type {
  Backend,
  BatchRowCells,
  CellChange,
  Etag,
  FetchedCells,
  RowChange,
  SurfaceKind,
  Unsubscribe,
} from "./rows-backend.ts";
import type { CellEntry, CellValue } from "../core/dtype.ts";
import type { Json } from "../core/json.ts";
import type { RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";
import { RowId } from "../ids/row-id.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { CellDataStore } from "../stores/source-row-cell-data-store.ts";
import { CellId } from "../ids/ids.ts";
import { SheetId } from "../ids/sheet-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";

/**
 * Minimal lookup surface the adapter needs to resolve a row's
 * canonical lookupKey. A sheet's lookup is built from
 * `sheetInternals(sheet).canonicalCells`; tests can stub it.
 */
export interface CanonicalLookup {
  readonly canonicalCells: CellDataStore<string>;
  rowMeta(id: RowLocalId): { readonly lookupKey: Option<string> };
  readonly registry: { resolveFromRawId(raw: string): RowLocalId };
}

/**
 * Check whether a lookupKey is canonical (server-route-backed).
 * Mirrors `SourceRow.canonicalKey` — exported so external callers
 * can share the classification. `null` / non-string values are
 * non-canonical.
 *
 * Two recognized shapes:
 *   - bare `:~rest`           — the original form
 *   - `[provider]:~rest`      — provider-namespaced canonical key
 *                               (e.g. `[weather@lona.so]:~weather:us-sf`)
 *
 * Both route the row's cells through the shared canonical store;
 * both reject local writes via
 * `SourceRow.write`.
 */
export function isCanonicalKey(key: unknown): key is string {
  return typeof key === "string" && CANONICAL_KEY_RE.test(key);
}

const CANONICAL_KEY_RE = /^(\[[^\]]+\])?:~/;

/** One canonical resident's fetch state: the ranges fetched into it and the reads in flight. */
type ResidentFetches = {
  coverage: DateTime.Range<Utc>[];
  readonly pending: {
    readonly range: DateTime.Range<Utc>;
    readonly promise: Promise<void>;
    readonly current: () => boolean;
  }[];
};

/**
 * Completed coverage and active fetches belong to the resident facts, so
 * forget/eviction retires both without retaining a permanent canonical-key map.
 * Adapters sharing one physical resident still share its range coverage. Keyed
 * by the resident a read lease captured (`captureRead(key).resident`), held as
 * the opaque object it is: the backend reads no fact through it.
 */
const RESIDENT_FETCHES = new WeakMap<object, ResidentFetches>();

/** `resident`'s fetch state, created on its first fetch. */
function fetchesOf(resident: object): ResidentFetches {
  let fetches = RESIDENT_FETCHES.get(resident);
  if (fetches === undefined) {
    fetches = { coverage: [], pending: [] };
    RESIDENT_FETCHES.set(resident, fetches);
  }
  return fetches;
}

function mergeCoverage(ranges: DateTime.Range<Utc>[], incoming: DateTime.Range<Utc>): DateTime.Range<Utc>[] {
  const sorted = [...ranges, incoming].sort((a, b) => a.start.mse - b.start.mse);
  const merged: DateTime.Range<Utc>[] = [];
  for (const range of sorted) {
    const previous = merged[merged.length - 1];
    if (previous && previous.end.mse >= range.start.mse) {
      merged[merged.length - 1] = new DateTime.Range(previous.start,
        previous.end.mse >= range.end.mse ? previous.end : range.end);
    } else merged.push(range);
  }
  return merged;
}

/**
 * A `Backend` wrapper that routes canonical provider rows (`:~…` lookup keys) through the client's
 * shared cell store, so sheets share their fetches.
 */
export class CanonicalBackend implements Backend {
  readonly #inner: Backend;
  readonly #lookup: CanonicalLookup;
  constructor(
    inner: Backend,
    lookup: CanonicalLookup,
  ) {
    this.#lookup = lookup;
    this.#inner = inner;
  }

  fetchSheet(sheetKey: SheetNetworkKey): Promise<Option<RawSheet>> {
    return this.#inner.fetchSheet(sheetKey);
  }

  /**
   * Passes through: the canonical store is filled by row reads
   * (`fetchCells`), and a keyed read names no row of this sheet.
   */
  fetchCellsByKey(key: RowNetworkKey, range: NaiveDate.Range): Promise<FetchedCells> {
    return this.#inner.fetchCellsByKey(key, range);
  }

  async fetchCells(
    rowId: RowId,
    range: NaiveDate.Range,
  ): Promise<FetchedCells> {
    const key = this.#canonicalKeyFor(rowId);
    if (key == null) return this.#inner.fetchCells(rowId, range);

    const store = this.#lookup.canonicalCells;
    const utcRange = range.withTime().withTz(Utc);
    const lease = store.captureRead(key);
    const resident = lease.resident;
    const checkCurrent = (): void => {
      if (!lease.current()) {
        throw new Error("CanonicalBackend.fetchCells: discarded canonical row " + key);
      }
    };
    let failed = false;
    try {
      checkCurrent();
      const fullyCovered = (RESIDENT_FETCHES.get(resident)?.coverage ?? []).some((covered) =>
        covered.start.mse <= utcRange.start.mse && covered.end.mse >= utcRange.end.mse);

      if (!fullyCovered) {
        const fetches = fetchesOf(resident);
        const pending = fetches.pending;
        const containing = pending.find((read) => read.current() &&
          read.range.start.mse <= utcRange.start.mse && read.range.end.mse >= utcRange.end.mse);
        if (containing) {
          await containing.promise;
        } else {
          // Register before invoking the backend, including synchronous reentrancy.
          const promise = Promise.resolve().then(async () => {
            checkCurrent();
            const fetched = await this.#inner.fetchCells(RowId.of(key), range);
            checkCurrent();
            store.asInstall(() => {
              for (const cell of fetched.cells) {
                checkCurrent();
                if (store.writtenLocallySince(key, lease.revision, cell.date, cell.id ?? null)) continue;
                store.set(key, cell.date, cell.value, cell.id ?? null, null, null);
                checkCurrent();
              }
              for (const entry of fetched.custom) {
                checkCurrent();
                if (store.customWrittenLocallySince(key, lease.revision, entry.id)) continue;
                store.setCustom(key, entry, null);
                checkCurrent();
              }
            });
            checkCurrent();
            fetches.coverage = mergeCoverage(fetches.coverage, utcRange);
            checkCurrent();
          });
          const read = { range: utcRange, promise, current: lease.current };
          pending.push(read);
          try {
            await promise;
          } finally {
            const index = pending.indexOf(read);
            if (index >= 0) pending.splice(index, 1);
          }
        }
      }

      // Every caller, including cache hits and joiners, owns its snapshot lease.
      checkCurrent();
      const cells = [...store.cells(key, range)];
      checkCurrent();
      const custom = [...store.customCells(key, utcRange)];
      checkCurrent();
      return { cells, custom };
    } catch (error) {
      failed = true;
      throw error;
    } finally {
      try {
        lease.release();
      } catch (error) {
        // A cleanup failure must not replace the originating read failure.
        if (!failed) throw error;
      }
    }
  }

  /** Passes through: the answer carries wire rows, which name their own keys. */
  fetchSheetCells(
    sheetKey: SheetNetworkKey,
    range: NaiveDate.Range,
  ): Promise<SheetsNetwork.SheetRowDataResponse[]> {
    return this.#inner.fetchSheetCells(sheetKey, range);
  }

  fetchCellsBatch(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>> {
    // Phase A: passthrough. Phase B will populate `canonicalCells`
    // with each `ok` row's cells so subscribing sheets see them via
    // their existing `canonicalCells.subscribeAll` fan-out.
    return this.#inner.fetchCellsBatch(sheetKey, rowKeys, range);
  }

  onRowChange(handler: (change: RowChange) => void): Unsubscribe {
    return this.#inner.onRowChange(handler);
  }

  /**
   * Subscribe to cell changes for `rowId`. For canonical rows the
   * subscription attaches to the Client-scoped store so one push
   * reaches every subscribing sheet via each sheet's own sink
   * (see `Sheet` constructor's `canonicalCells.subscribeAll`).
   * Direct subscribers of `onCellChange` (e.g. an integration
   * layer that wants a pre-translated `CellChange` stream) also
   * see those pushes, translated from the store's change shape.
   *
   * Non-canonical rows delegate straight to the inner backend.
   */
  onCellChange(
    rowId: RowId,
    handler: (change: CellChange) => void,
  ): Unsubscribe {
    const key = this.#canonicalKeyFor(rowId);
    if (key == null) return this.#inner.onCellChange(rowId, handler);

    return this.#lookup.canonicalCells.subscribe(key, (change) => {
      // Datetime (custom) cells don't map to the date-anchored
      // `CellChange`; the handle receives them via the canonical→handle
      // mirror in `SheetDataSource.attachCanonicalStore`.
      if (change.kind === "set-custom" || change.kind === "clear-custom") {
        return;
      }
      if (change.kind !== "set") {
        handler(
          change.cellId === undefined
            ? { kind: "clear", date: change.date }
            : { kind: "clear", date: change.date, id: change.cellId },
        );
        return;
      }
      handler({ kind: "set", cell: change.entry });
    });
  }

  writeCell(
    rowId: RowId,
    cell: CellEntry,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    return this.#inner.writeCell(rowId, cell, expectedEtag, requestId);
  }

  clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    return this.#inner.clearCell(rowId, date, id, expectedEtag);
  }

  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    return this.#inner.writeTimepointCell(
      rowId,
      dateRange,
      data,
      replaceCellId,
      expectedEtag,
    );
  }

  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    return this.#inner.clearTimepointCell(rowId, cellId, expectedEtag);
  }

  addRow(
    row: RawRow,
    sheet: Option<SheetId>,
    position: Option<number>,
    expectedEtag: Option<Etag>,
    surface: Option<SurfaceKind>,
  ): Promise<Option<RowId>> {
    return this.#inner.addRow(row, sheet, position, expectedEtag, surface);
  }

  updateRow(
    rowId: RowId,
    patch: Backend.UpdateRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Backend.RowUpdated> {
    return this.#inner.updateRow(rowId, patch, expectedEtag);
  }

  deleteRow(rowId: RowId, expectedEtag: Option<Etag>): Promise<void> {
    return this.#inner.deleteRow(rowId, expectedEtag);
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  #canonicalKeyFor(rowId: RowId): Option<string> {
    const localId = this.#lookup.registry.resolveFromRawId(rowId.toString());
    const key = this.#lookup.rowMeta(localId).lookupKey ?? null;
    return isCanonicalKey(key) ? key : null;
  }
}
